import { useState } from "react";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { TagNumericStrip, TagRemovableDemo } from "./TagPlaygroundDemos";
import {
    canUseColor,
    canUseLeftIcon,
    canUseNumeric,
    canUseRemovable,
    defaultTagSandboxState,
    legalizeTagSandbox,
    tagColors,
    tagSizes,
    type TagColor,
    type TagSandboxState,
    type TagSize,
} from "./TagPlayground.lib";
import { TagContentPaddingDemo } from "./TagPlaygroundContent";
import { TagMatrix } from "./TagPlaygroundMatrix";
import { TagSandboxPreview } from "./TagPlaygroundSandbox";
import { useAnatomyMode } from "./componentAnatomyMode";
import { BooleanControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DatePickerPlayground.module.scss";

export const TagPlayground = () => {
    const [sandbox, setSandbox] = useState<TagSandboxState>(
        defaultTagSandboxState,
    );

    const updateSandbox = (patch: Partial<TagSandboxState>) => {
        setSandbox((current) => legalizeTagSandbox({ ...current, ...patch }));
    };

    const {
        size,
        color,
        isOutline,
        isNumeric,
        hasLeftIcon,
        isRemovable,
        isClickable,
    } = sandbox;

    const isNumericEnabled = canUseNumeric(size) && !isRemovable;
    const isLeftIconEnabled = canUseLeftIcon({ size, isNumeric });
    const isRemovableEnabled = canUseRemovable({ size, isNumeric });

    // Tag has no disabled state; the anatomy spec only knows resting + hover.
    // The content mirrors TagSandboxPreview: xs is always numeric ("1"),
    // numeric tags read "12", everything else reads "Tag".
    const isNumericShown = isNumeric || size === "xs";
    let schematicLabel = "Tag";
    if (isNumericShown) {
        schematicLabel = size === "xs" ? "1" : "12";
    }
    const anatomy = useAnatomyMode({
        component: "tag",
        variantId: isOutline ? `${color}-outline` : color,
        sizeId: size,
        isDisabled: false,
        tagContent: {
            label: schematicLabel,
            hasLeftIcon,
            isNumeric: isNumericShown,
            isRemovable,
        },
    });
    const forcedState = anatomy.forcedState === "hover" ? "hover" : undefined;

    const summary = `${size} · ${color}${isOutline ? " · outline" : ""}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Size</h2>
                        <ToggleGroup.Root<TagSize>
                            value={size}
                            onValueChange={(value) => {
                                if (value) {
                                    updateSandbox({ size: value });
                                }
                            }}
                        >
                            {tagSizes.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                        <h2 className={styles.subheading}>Color</h2>
                        <ToggleGroup.Root<TagColor>
                            value={color}
                            onValueChange={(value) => {
                                if (value) {
                                    updateSandbox({ color: value });
                                }
                            }}
                        >
                            {tagColors
                                .filter((option) => canUseColor(size, option))
                                .map((option) => (
                                    <ToggleGroup.Item
                                        key={option}
                                        value={option}
                                    >
                                        {option}
                                    </ToggleGroup.Item>
                                ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Outline"
                                checked={isOutline}
                                onChange={(nextIsOutline) => {
                                    updateSandbox({ isOutline: nextIsOutline });
                                }}
                            />
                            <BooleanControl
                                label="Numeric"
                                checked={isNumeric}
                                disabled={!isNumericEnabled}
                                onChange={(nextIsNumeric) => {
                                    updateSandbox({ isNumeric: nextIsNumeric });
                                }}
                            />
                            <BooleanControl
                                label="Left icon"
                                checked={hasLeftIcon}
                                disabled={!isLeftIconEnabled}
                                onChange={(nextHasLeftIcon) => {
                                    updateSandbox({
                                        hasLeftIcon: nextHasLeftIcon,
                                    });
                                }}
                            />
                            <BooleanControl
                                label="Removable"
                                checked={isRemovable}
                                disabled={!isRemovableEnabled}
                                onChange={(nextIsRemovable) => {
                                    updateSandbox({
                                        isRemovable: nextIsRemovable,
                                    });
                                }}
                            />
                            <BooleanControl
                                label="Clickable"
                                checked={isClickable}
                                onChange={(nextIsClickable) => {
                                    updateSandbox({
                                        isClickable: nextIsClickable,
                                    });
                                }}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={anatomy.isActive ? `x-ray · ${summary}` : summary}
                belowStage={
                    <>
                        <TagMatrix />
                        <TagContentPaddingDemo />
                        <TagRemovableDemo />
                        <TagNumericStrip />
                    </>
                }
                output={JSON.stringify(
                    {
                        size,
                        color,
                        outline: isOutline,
                        numeric: isNumeric,
                        leftIcon: hasLeftIcon,
                        removable: isRemovable,
                        clickable: isClickable,
                        forcedState: forcedState ?? null,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <TagSandboxPreview {...sandbox} forcedState={forcedState} />
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
